import {
  ITEM_CARD_NARRATIVE_LIST_VIEW,
  ITEM_CARD_SEQUENCE_LIST_VIEW,
  NARRATIVE_VIEW,
  SPREADSHEET_VIEW
} from "../../constants/identifiers";
import interfaceIdentifiers from "../../constants/interfaceIdentifiers";
import itemTypeIds from "../../constants/itemTypeIds";
import { definitionSelectors } from "../../modules/definition";
import { typeSelectors } from "../../modules/definition/types";
import { itemActionTypes, itemSelectors } from "../../modules/items";
import { narrativeSelectors } from "../../modules/narrative";
import preprocessorQuery from "../../utils/preprocessorQuery";
import {
  getEnterEditModeInstruction,
  getFlashPlaceholder,
  getIconPlaceholder,
  getSplitViewInstruction,
  getSwitchViewInstruction,
  getTipIdGenerator,
  getViewNameLabel,
  Placeholders
} from "../utils";

const isDesktop = preprocessorQuery.isDesktop();
const isTablet = preprocessorQuery.isTablet();
const isMobile = preprocessorQuery.isMobile();

// -----------------------------------------------------
// IMPORTANT NOTES:
//
// GROUP_GATE / accessGate:
//    * expects a function with signature:
//        state => { return true; }
//    * return true to show tutorial
//    * return false to block tutorial and show message
//    * if function is null, will show tutorial
//
// resolve:
//    * expects a function with signature:
//       (action, newState, oldState) => { return true; }
//    * return true to mark the tip as resolved
//    * batch actions will be broken down by the time
//      this function is called, so you will receive the
//      individual actions
//
// -----------------------------------------------------

// Group ID
const GROUP_ID = "TELL_A_STORY";
const GROUP_LABEL = "Tell a Story";
const GROUP_DESCRIPTION = "Decide which elements belong in your story, and arrange them into a narrative structure.";
const GROUP_GATE = state =>
  definitionSelectors.isNarrativeAllowed(state) &&
  itemSelectors.getAllEventIds(state).size >= 5 &&
  typeSelectors.getOrderedNarrativeTypes(state).size >= 2;
const GROUP_GATE_MESSAGE =
  "You must have Track Independent Narrative enabled and at least five events to begin this tutorial.";
const GROUP_APPLICABLE_GATE = state => definitionSelectors.isNarrativeAllowed(state);
const GROUP_APPLICABLE_GATE_MESSAGE =
  "This tutorial only applies to timelines with Track Independent Narrative enabled.";
const GROUP_COMPLETED_MESSAGE = `Great work! Continue adding to your narrative to build out your story. If you plan to write in ${Placeholders.AVAILABLE_WRITING_APPS}, now might be the time to think about syncing`;
const GROUP_LINKS = [
  {
    label: "Learn other ways to view your narrative",
    path: "https://help.timeline.app/article/162-narrative-and-outline-views"
  },
  {
    label: "Chronological vs Narrative Order",
    path: "https://help.timeline.app/article/149-chronological-vs-narrative-order"
  }
];

// Create an ID generator
// this will make IDs of the form: "GROUP_ID / TIP_ID"
const idGenerator = getTipIdGenerator(GROUP_ID);

let tips = [];
if (isDesktop) {
  tips = [
    {
      id: idGenerator("ADD_EXISTING_ITEM_NARRATIVE"),
      label: `Drag existing ${Placeholders.EVENT_PLURAL_LABEL} into your Narrative`,
      steps: [
        getSplitViewInstruction(SPREADSHEET_VIEW, NARRATIVE_VIEW),
        {
          label: `Select the ${Placeholders.EVENT_PLURAL_LABEL} that you would like to include in your narrative`,
          isNumbered: true
        },
        {
          label: `Use the ${getFlashPlaceholder(
            interfaceIdentifiers.TABLE_VIEW_DRAG_HANDLE,
            "drag handle"
          )} ${getIconPlaceholder("dragHandle")} to drag the ${Placeholders.EVENT_PLURAL_LABEL} into Narrative View`,
          isNumbered: true
        }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true,
      dismissalGate: state => narrativeSelectors.getNarrativeHasItems(state)
    },
    {
      id: idGenerator("ADD_EVENT_NARRATIVE"),
      label: "Create items directly inside your narrative",
      steps: [
        {
          label: `In ${getViewNameLabel(
            NARRATIVE_VIEW
          )} View, scroll to the far end and click on the "+ Add" button to create a new item`,
          isNumbered: true
        },
        {
          label: `Choose "${Placeholders.NARRATIVE_ITEM_CAPITAL_LABEL}" to create items that will also appear in your Spreadsheet`,
          isNumbered: true
        },
        {
          label: `Choose "${Placeholders.NARRATIVE_EXPOSITION_CAPITAL_LABEL}" to create narrative-only items that won't appear anywhere else`,
          isNumbered: true
        },
        { label: "After choosing the item type, type a label for your item and press enter.", isNumbered: true }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true,
      dismissalGate: state => narrativeSelectors.getNarrativeHasItems(state)
    },
    {
      id: idGenerator("REORDER_NARRATIVE"),
      label: "Reorder your narrative",
      steps: [
        {
          label: `The narrative has its own independent order of ${Placeholders.EVENT_PLURAL_LABEL}`,
          isNumbered: true
        },
        { label: `Click and hold ${Placeholders.AN_EVENT_LABEL} to drag it.`, isNumbered: true },
        { label: `Drag the ${Placeholders.EVENT_LABEL} to a new position in the narrative.`, isNumbered: true },
        {
          label: `Moving an item in the narrative does not affect its position in the Timeline or Spreadsheet`,
          isNumbered: false
        }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true,
      dismissalGate: state => narrativeSelectors.getNarrativeHasMultipleItems(state)
    },
    {
      id: idGenerator("ADD_NARRATIVE_FOLDER"),
      label: `Organise your narrative into folders such as ${Placeholders.NARRATIVE_FOLDER_SECTION_TYPES}`,
      steps: [
        {
          label: "In Narrative View, click the + Add button, choose Narrative Folder, and type a label.",
          isNumbered: true
        },
        { label: "Drag the folder to the beginning of the narrative", isNumbered: true },
        {
          label: `Drag other items from your narrative and drop them onto the folder to place them inside`,
          isNumbered: true
        }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true,
      dismissalGate: state =>
        narrativeSelectors.getNarrativeDepth(state) > 0 &&
        itemSelectors.getItemIdsOfType(state, itemTypeIds.NARRATIVE_FOLDER_TYPE_ID).size > 0
    },
    {
      id: idGenerator("SET_NARRATIVE_SECTION_INSPECTOR"),
      label: "Change an item’s narrative section type in the Inspector",
      steps: [
        {
          label: `Narrative section types change as you move items in your narrative, but you can override this`,
          isNumbered: false
        },
        { label: `Select ${Placeholders.AN_EVENT_LABEL} or folder in your narrative view`, isNumbered: true },
        {
          label: `In the Inspector, click on the ${getFlashPlaceholder(
            interfaceIdentifiers.INSPECTOR_DATES_TAB,
            "clock icon"
          )} ${getIconPlaceholder("inspectorTime")} to show the Date & Narrative tab`,
          isNumbered: true
        },
        {
          label: `Click the ${getFlashPlaceholder(
            interfaceIdentifiers.INSPECTOR_NARRATIVE_SECTION_TYPE,
            "Narrative Section Type"
          )} field and change it to a fixed value`,
          isNumbered: true
        },
        {
          label: `Available section types and automation properties can be changed in  ${getFlashPlaceholder(
            interfaceIdentifiers.SETTINGS,
            "Settings"
          )} ${getIconPlaceholder("sidebarSettings")}`,
          isNumbered: false
        }
      ],
      requiresInspector: true,
      accessGate: null,
      canSkip: true,
      resolve: action => action.type === itemActionTypes.SET_NARRATIVE_LABEL
    }
  ];
} else if (isTablet) {
  tips = [
    {
      id: idGenerator("ADD_EXISTING_ITEM_NARRATIVE"),
      label: `Drag existing ${Placeholders.EVENT_PLURAL_LABEL} into your Narrative`,
      steps: [
        getSplitViewInstruction(SPREADSHEET_VIEW, NARRATIVE_VIEW),
        getEnterEditModeInstruction(SPREADSHEET_VIEW),
        {
          label: `Select the ${Placeholders.EVENT_PLURAL_LABEL} that you would like to include in your narrative`,
          isNumbered: true
        },
        {
          label: `Use the ${getFlashPlaceholder(
            interfaceIdentifiers.TABLE_VIEW_DRAG_HANDLE,
            "drag handle"
          )} ${getIconPlaceholder("dragHandle")} to drag the ${Placeholders.EVENT_PLURAL_LABEL} into Narrative View`,
          isNumbered: true
        }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true,
      dismissalGate: state => narrativeSelectors.getNarrativeHasItems(state)
    },
    {
      id: idGenerator("ADD_EVENT_NARRATIVE"),
      label: "Create items directly inside your narrative",
      steps: [
        {
          label: `In ${getViewNameLabel(
            NARRATIVE_VIEW
          )} View, scroll to the far end and tap on the "+ Add" button to create a new item`,
          isNumbered: true
        },
        {
          label: `Choose "${Placeholders.NARRATIVE_ITEM_CAPITAL_LABEL}" to create items that will also appear in your Spreadsheet`,
          isNumbered: true
        },
        {
          label: `Choose "${Placeholders.NARRATIVE_EXPOSITION_CAPITAL_LABEL}" to create narrative-only items that won't appear anywhere else`,
          isNumbered: true
        },
        { label: "After choosing the item type, type a label for your item and press enter.", isNumbered: true }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true,
      requiresExitEditMode: true,
      dismissalGate: state => narrativeSelectors.getNarrativeHasItems(state)
    },
    {
      id: idGenerator("REORDER_NARRATIVE"),
      label: "Reorder your narrative",
      steps: [
        {
          label: `The narrative has its own independent order of ${Placeholders.EVENT_PLURAL_LABEL}`,
          isNumbered: true
        },
        getEnterEditModeInstruction(NARRATIVE_VIEW),
        { label: `Press and hold ${Placeholders.AN_EVENT_LABEL} to drag it.`, isNumbered: true },
        { label: `Drag the ${Placeholders.EVENT_LABEL} to a new position in the narrative.`, isNumbered: true },
        {
          label: `Moving an item in the narrative does not affect its position in the Timeline or Spreadsheet`,
          isNumbered: false
        }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true,
      dismissalGate: state => narrativeSelectors.getNarrativeHasMultipleItems(state)
    },
    {
      id: idGenerator("ADD_NARRATIVE_FOLDER"),
      label: `Organise your narrative into folders such as ${Placeholders.NARRATIVE_FOLDER_SECTION_TYPES}`,
      steps: [
        {
          label: "In Narrative View, tap the + Add button, choose Narrative Folder, and type a label.",
          isNumbered: true
        },
        getEnterEditModeInstruction(NARRATIVE_VIEW),
        { label: "Drag the folder to the beginning of the narrative", isNumbered: true },
        {
          label: `Drag other items from your narrative and drop them onto the folder to place them inside`,
          isNumbered: true
        }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true,
      dismissalGate: state =>
        narrativeSelectors.getNarrativeDepth(state) > 0 &&
        itemSelectors.getItemIdsOfType(state, itemTypeIds.NARRATIVE_FOLDER_TYPE_ID).size > 0
    },
    {
      id: idGenerator("SET_NARRATIVE_SECTION_INSPECTOR"),
      label: "Change an item’s narrative section type in the Inspector",
      steps: [
        {
          label: `Narrative section types change as you move items in your narrative, but you can override this`,
          isNumbered: false
        },
        { label: `Select ${Placeholders.AN_EVENT_LABEL} or folder in your narrative view`, isNumbered: true },
        { label: `In the Inspector, tap on "Dates & Narrative"`, isNumbered: true },
        {
          label: `Tap the ${getFlashPlaceholder(
            interfaceIdentifiers.INSPECTOR_NARRATIVE_SECTION_TYPE,
            "Narrative Section Type"
          )} field and change it to a fixed value`,
          isNumbered: true
        },
        {
          label: `Available section types and automation properties can be changed in  ${getFlashPlaceholder(
            interfaceIdentifiers.SETTINGS,
            "Settings"
          )} ${getIconPlaceholder("sidebarSettings")}`,
          isNumbered: false
        }
      ],
      requiresInspector: true,
      accessGate: null,
      canSkip: true,
      resolve: action => action.type === itemActionTypes.SET_NARRATIVE_LABEL
    }
  ];
} else if (isMobile) {
  tips = [
    {
      id: idGenerator("ADD_EXISTING_ITEM_NARRATIVE"),
      label: `Add existing ${Placeholders.EVENT_PLURAL_LABEL} into your Narrative`,
      steps: [
        getSwitchViewInstruction(ITEM_CARD_SEQUENCE_LIST_VIEW),
        getEnterEditModeInstruction(ITEM_CARD_SEQUENCE_LIST_VIEW),
        {
          label: `Select the ${Placeholders.EVENT_PLURAL_LABEL} that you would like to include in your narrative`,
          isNumbered: true
        },
        {
          label: `Tap on the more icon ${getIconPlaceholder("ellipsis")} in the toolbar and choose "Add to Narrative"`,
          isNumbered: true
        },
        {
          label: `Rotate your device to portrait mode and open Narrative Outline to see the events you added`,
          isNumbered: true
        }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true,
      dismissalGate: state => narrativeSelectors.getNarrativeHasItems(state)
    },
    {
      id: idGenerator("ADD_EVENT_NARRATIVE"),
      label: "Create items directly inside your narrative",
      steps: [
        getSwitchViewInstruction(ITEM_CARD_NARRATIVE_LIST_VIEW),
        { label: `Tap the "+ Add" button in the top right corner`, isNumbered: true },
        {
          label: `Choose "${Placeholders.NARRATIVE_ITEM_CAPITAL_LABEL}" to create items that will also appear in your Spreadsheet`,
          isNumbered: true
        },
        {
          label: `Choose "${Placeholders.NARRATIVE_EXPOSITION_CAPITAL_LABEL}" to create narrative-only items that won't appear anywhere else`,
          isNumbered: true
        },
        { label: "After choosing the item type, type a label for your item and press enter.", isNumbered: true }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true,
      requiresExitEditMode: true,
      dismissalGate: state => narrativeSelectors.getNarrativeHasItems(state)
    },
    {
      id: idGenerator("REORDER_NARRATIVE"),
      label: "Reorder your narrative",
      steps: [
        {
          label: `The narrative has its own independent order of ${Placeholders.EVENT_PLURAL_LABEL}`,
          isNumbered: true
        },
        getEnterEditModeInstruction(ITEM_CARD_NARRATIVE_LIST_VIEW),
        {
          label: `Use the drag handles ${getIconPlaceholder("dragHandle")} next to ${
            Placeholders.EVENT_PLURAL_LABEL
          } to drag them to a new position`,
          isNumbered: true
        },
        {
          label: `Drop between ${Placeholders.EVENT_PLURAL_LABEL} to re-order, or on top of ${Placeholders.EVENT_PLURAL_LABEL} to nest within`,
          isNumbered: true
        },
        {
          label: `Moving an item in the narrative does not affect its position in the Timeline or Spreadsheet`,
          isNumbered: false
        }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true,
      dismissalGate: state => narrativeSelectors.getNarrativeHasMultipleItems(state)
    },
    {
      id: idGenerator("ADD_NARRATIVE_FOLDER"),
      label: `Organise your narrative into folders such as ${Placeholders.NARRATIVE_FOLDER_SECTION_TYPES}`,
      steps: [
        {
          label: "In Narrative Outline View, tap the + Add button, choose Narrative Folder, and type a label.",
          isNumbered: true
        },
        getEnterEditModeInstruction(ITEM_CARD_NARRATIVE_LIST_VIEW),
        { label: "Drag the folder to the beginning of the narrative", isNumbered: true },
        {
          label: `Drag other items from your narrative and drop them onto the folder to place them inside`,
          isNumbered: true
        }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true,
      dismissalGate: state =>
        narrativeSelectors.getNarrativeDepth(state) > 0 &&
        itemSelectors.getItemIdsOfType(state, itemTypeIds.NARRATIVE_FOLDER_TYPE_ID).size > 0
    },
    {
      id: idGenerator("SET_NARRATIVE_SECTION_INSPECTOR"),
      label: "Change an item’s narrative section type in the Inspector",
      steps: [
        {
          label: `Narrative section types change as you move items in your narrative, but you can override this`,
          isNumbered: false
        },
        { label: `Select ${Placeholders.AN_EVENT_LABEL} or folder in your narrative view`, isNumbered: true },
        { label: `In the Inspector, tap on "Dates & Narrative"`, isNumbered: true },
        {
          label: `Tap the ${getFlashPlaceholder(
            interfaceIdentifiers.INSPECTOR_NARRATIVE_SECTION_TYPE,
            "Narrative Section Type"
          )} field and change it to a fixed value`,
          isNumbered: true
        },
        {
          label: `Available section types and automation properties can be changed in  ${getFlashPlaceholder(
            interfaceIdentifiers.SETTINGS,
            "Settings"
          )} ${getIconPlaceholder("sidebarSettings")}`,
          isNumbered: false
        }
      ],
      requiresInspector: true,
      accessGate: null,
      canSkip: true,
      resolve: action => action.type === itemActionTypes.SET_NARRATIVE_LABEL
    },
    {
      id: idGenerator("ROTATE_TO_NARRATIVE_VIEW"),
      label: `Rotate your screen to see visual representation`,
      steps: [
        getSwitchViewInstruction(NARRATIVE_VIEW),
        { label: `This will show a visual representation of your narrative`, isNumbered: true }
      ],
      accessGate: null,
      gateMessage: null,
      canSkip: true,
      requiresDismissal: true
    }
  ];
}

export default {
  id: GROUP_ID,
  label: GROUP_LABEL,
  description: GROUP_DESCRIPTION,
  accessGate: GROUP_GATE,
  gateMessage: GROUP_GATE_MESSAGE,
  applicableGate: GROUP_APPLICABLE_GATE,
  gateApplicableMessage: GROUP_APPLICABLE_GATE_MESSAGE,
  completedMessage: GROUP_COMPLETED_MESSAGE,
  links: GROUP_LINKS,
  tips
};
